import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { useSession, ULTIMATE_VISIBLE } from "./SessionProvider";
import { useAuth } from "./AuthProvider";
import { X, Crown, Check, Eye, Zap, Clock, Loader2, AlertTriangle } from "lucide-react";
import { ProCompareTable } from "@/components/ProCompareTable";
import { useToast } from "@/hooks/use-toast";
import { startSubscribe } from "@/lib/billing";
import { isNative, getPlatform } from "@/lib/platform";
import { getIapDiagnostics, restoreCompleteDescription, type IapDiagnostics } from "@/lib/iap";
import { usePrices, PRICE_PLACEHOLDER, formatPlanLine } from "@/hooks/usePrices";
import { LegalReader } from "./LegalReader";

// Pro is a self-serve subscription. Ultimate is hidden behind ULTIMATE_VISIBLE;
// surfaced when the live battle overlay (Power Scouter) is ready for prime time.
//
// Every benefit listed below MUST be a feature that actually ships in the
// current build. Advertising a parked feature (anything behind a false flag in
// lib/featureFlags.ts) is a store-review risk, not just a copy nit. Re-check
// this list whenever a feature flag flips — Storage Maximisation is a Pro
// feature now. (Storage Maximisation itself was deleted 2026-08-21.)
export function PaywallModal() {
  const { paywallOpen, paywallReason, closePaywall, purchasePro, restorePurchases } = useSession();
  const [plan, setPlan] = useState<"yearly" | "monthly" | "ultimate">("yearly");
  const { toast } = useToast();
  const { authedFetch } = useAuth();
  const [, setLocation] = useLocation();
  const [busy, setBusy] = useState(false);
  // Apple Guideline 3.1.2 requires the subscription screen to carry functional
  // links to the Terms of Use (EULA) and Privacy Policy. These open the bundled
  // LegalReader (the same Terms + Privacy pack surfaced in Settings), so the
  // links resolve offline and never depend on a live URL. null = closed.
  const [legalTab, setLegalTab] = useState<null | "tos" | "privacy">(null);
  // Live RC prices. Never hardcode a "$" literal in the JSX below; every
  // price rendered here comes from `prices` (live offering first, canonical
  // fallback second) so localization is correct and nothing drifts if ASC
  // pricing changes.
  const { prices } = usePrices();
  // The store this build sells through (App Review 2026-10-07, guideline
  // 2.3.10): an iOS binary names the App Store only, Android names Google
  // Play only; the web, which cannot buy, says "app store".
  const platform = getPlatform();
  const storeLabel = platform === "ios" ? "App Store" : platform === "android" ? "Google Play" : "app store";
  // IAP activation probe. On native we no longer assume purchases work just
  // because we're in a native shell — if the RevenueCat key / plugin /
  // offering is missing the CTA would fail with a vague toast. We probe on
  // open and, when disabled, swap the CTA for an honest unavailable card
  // that points the trainer at the diagnostics panel.
  const [iapDiag, setIapDiag] = useState<IapDiagnostics | null>(null);
  useEffect(() => {
    if (!paywallOpen || !isNative()) return;
    let cancelled = false;
    getIapDiagnostics()
      .then((d) => {
        if (!cancelled) setIapDiag(d);
      })
      .catch(() => {
        /* never block the paywall on a diagnostics read */
      });
    return () => {
      cancelled = true;
    };
  }, [paywallOpen]);

  if (!paywallOpen) return null;

  // Workstream C launch gate: even if the plan state somehow gets stuck on
  // "ultimate" from a prior session, the flag flip below collapses it back
  // to "yearly" before the user can purchase. Defence in depth on top of
  // the ULTIMATE_VISIBLE gate around the plan card itself.
  const safePlan: "yearly" | "monthly" =
    plan === "ultimate" && !ULTIMATE_VISIBLE ? "yearly" : (plan as "yearly" | "monthly");

  async function subscribe() {
    if (plan === "ultimate") {
      if (!ULTIMATE_VISIBLE) {
        // The card is hidden in the launch build — this branch should be
        // unreachable, but if a stale state ever lands here we silently
        // route the user back to the yearly Pro plan instead of leaking
        // an Ultimate marketing toast.
        setPlan("yearly");
        return;
      }
      toast({
        title: "Ultimate is launching later",
        description: "We'll surface Ultimate the moment Power Scouter is ready for prime time.",
      });
      return;
    }
    setBusy(true);
    try {
      // Native IAP path: on iOS / Android this drives the RevenueCat store
      // sheet. Web has no purchase flow (App Store / Google Play rules
      // require native IAP for digital subscriptions) and the purchase CTA
      // is hidden below via `purchasesAvailable`.
      if (isNative()) {
        const result = await purchasePro(safePlan === "yearly" ? "annual" : "monthly");
        if (result.ok) {
          toast({ title: "Pro unlocked", description: "Welcome aboard." });
          closePaywall();
          return;
        }
        if (result.message) {
          toast({
            title: "Purchase did not complete",
            description: result.message,
            variant: "destructive",
          });
        }
        return;
      }
      // Web fallback: surface a clear "use the app" message via startSubscribe
      // which now throws on web. The purchase CTA is gated below, so this
      // branch should normally be unreachable.
      const planCode = safePlan === "yearly" ? "pro_yearly" : "pro_monthly";
      const result = await startSubscribe(planCode, { authedFetch });
      if (result.purchased) {
        toast({ title: "Pro unlocked", description: "Welcome aboard." });
        closePaywall();
      }
    } catch (err: any) {
      toast({
        title: "Checkout unavailable",
        description: err?.message || "Could not start checkout. Please try again.",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  }

  async function restore() {
    if (busy) return;
    setBusy(true);
    try {
      await restorePurchases();
      toast({
        title: "Restore complete",
        description: restoreCompleteDescription(getPlatform()),
      });
    } finally {
      setBusy(false);
    }
  }

  // Pro is sold exclusively through native In-App Purchases. The web build
  // hides the purchase CTA entirely; users are pointed at the iOS / Android
  // app, which is also enforced by the throw in startSubscribe.
  //
  // push9: on native we additionally require IAP to be actually activated.
  // The probe is async, so we treat "not yet read" as available (optimistic)
  // and only flip to the unavailable card once the diagnostics read confirms
  // IAP is disabled (missing key / plugin / configure failure).
  const iapBlocked = isNative() && iapDiag?.iapDisabled === true;
  const purchasesAvailable = isNative() && !iapBlocked;

  return (
    <>
    <div
      role="dialog"
      aria-label="Unlock Nexus Dex Pro"
      aria-modal="true"
      className="fixed inset-0 z-[80] flex items-end md:items-center justify-center px-3 py-3 bg-black/75 backdrop-blur-sm anim-fade-up"
      onClick={closePaywall}
    >
      {/* v1.1.1-b1 (Fix 7): outer card no longer applies overflow-hidden
          to the whole modal, which previously clipped any content below
          the price-toggle on a 390x844 viewport (the entire trial /
          maybe-later / restore-purchase row was unreachable). The
          aurora overlay keeps its own inset overflow-hidden so the
          gradient blobs stay within the card border, but the content
          column below is now scrollable up to 90% of the viewport
          height with overflow-y: auto. */}
      {/* The CARD is the scroller (2026-09-11 owner screenshot: a nested
          flex column with overflow-y never took the touch on iOS, even once
          it was height-constrained). This is the pattern every other bottom
          sheet in the app uses and it scrolls on device. */}
      <div
        className="relative w-full max-w-md v2-neon-card anim-fade-scale max-h-[90dvh] overflow-y-auto overscroll-contain"
        onClick={(e) => e.stopPropagation()}
        style={{
          boxShadow: "0 30px 80px hsl(0 0% 0% / 0.85), 0 0 0 1px var(--accent-fill-glow), 0 0 40px var(--accent-fill-glow-subtle)",
          WebkitOverflowScrolling: "touch",
          touchAction: "pan-y",
        }}
        data-testid="paywall-scroll"
      >
        {/* v1.3.4: aurora overlays removed inside the Go Pro card. Card
            border / CTA carry the neon green; the background stays sheer
            black so the paywall reads as Nexus dark, not lime. */}

        <button
          data-testid="button-close-paywall"
          onClick={closePaywall}
          className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center z-10"
          style={{ background: "var(--surface-dark-9)", border: "1px solid var(--accent-fill-glow)", color: "var(--text-primary)" }}
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="relative p-6">
          {/* b19-ui-cosmetics: adopt the global Nexus Dex brand mark (the same
              <Logo variant="mark" /> call PR #271 standardised on the
              questionnaire header), replacing the old square tile logo. The
              close X keeps its top-right slot. */}
          <div className="flex items-center justify-center mb-4">
            <Logo size={30} withWordmark variant="mark" />
          </div>

          <div className="flex justify-center mb-3">
            <span className="v2-eyebrow"><span className="v2-eyebrow__dot" />GO PRO</span>
          </div>

          <h2 className="v2-display text-center text-[22px] leading-tight" style={{ color: "var(--text-primary)" }}>
            Unlock your full Dex.
          </h2>
          {paywallReason && (
            <p className="text-center text-xs mt-1.5" style={{ color: "var(--text-secondary)" }}>{paywallReason}</p>
          )}
          <p className="text-center text-[12px] mt-2 leading-relaxed px-2" style={{ color: "var(--text-secondary)" }}>
            Take your time. The Basic tier has plenty to explore.
          </p>

          <div className="mt-5 space-y-2.5">
            {ULTIMATE_VISIBLE && plan === "ultimate" ? (
              <>
                <BenefitRow
                  icon={<Eye className="w-4 h-4" />}
                  title="Power Scouter live overlay"
                  desc="Floating HUD over Pokémon GO that calls moves, switches and shields in real time during battles"
                />
                <BenefitRow
                  icon={<Zap className="w-4 h-4" />}
                  title="Priority Coach inference"
                  desc="Deep scans and battle reports run on the fast lane, ahead of the standard Pro queue"
                />
                <BenefitRow
                  icon={<Crown className="w-4 h-4" />}
                  title="Everything in Pro"
                  desc="Every Pro feature is included"
                />
              </>
            ) : (
              /* The Basic vs Pro table (owner direction, 2026-09-11): the same
                 rows as Settings > Why go Pro? and the website, in place of the
                 old bullet list, so every Pro surface tells one story. */
              <ProCompareTable testIdPrefix="paywall-pro" className="mt-0" />
            )}
          </div>

          <div className={`mt-6 grid ${ULTIMATE_VISIBLE ? "grid-cols-3" : "grid-cols-2"} gap-2`}>
            <PlanCard
              testid="plan-yearly"
              active={plan === "yearly"}
              onClick={() => setPlan("yearly")}
              eyebrow="Pro Yearly"
              price={prices.annual ?? PRICE_PLACEHOLDER}
              unit="/yr"
              foot="Best value"
              badge="Save 17%"
            />
            <PlanCard
              testid="plan-monthly"
              active={plan === "monthly"}
              onClick={() => setPlan("monthly")}
              eyebrow="Pro Monthly"
              price={prices.monthly ?? PRICE_PLACEHOLDER}
              unit="/mo"
              foot="Cancel anytime"
            />
            {ULTIMATE_VISIBLE && (
              /* Ultimate has no App Store Connect product yet, so there is no
                 offering to read a price from and this renders a placeholder.
                 Before flipping ULTIMATE_VISIBLE: create the subscription in
                 ASC, add its id to IAP_PRODUCT_IDS, map it in detectPeriod, and
                 surface it on PriceSet — never re-hardcode the number here. */
              <PlanCard
                testid="plan-ultimate"
                active={plan === "ultimate"}
                onClick={() => setPlan("ultimate")}
                eyebrow="Ultimate"
                price={PRICE_PLACEHOLDER}
                unit="/mo"
                foot="Power Scouter"
                badge="Top"
                accent="violet"
              />
            )}
          </div>

          <div
            className="mt-5 v2-neon-card-soft p-3.5 flex items-start gap-3"
            data-testid="section-pro-trial"
          >
            <div
              className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center"
              style={{
                background: "var(--accent-fill-glow-faint)",
                border: "1px solid var(--accent-fill-glow)",
                color: "var(--accent-fill)",
              }}
            >
              <Clock className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold leading-tight" style={{ color: "var(--text-primary)" }}>Cancel anytime</p>
              <p className="text-[11px] mt-0.5 leading-snug" style={{ color: "var(--text-secondary)" }}>
                Manage or cancel any time from your {storeLabel} subscriptions.
              </p>
            </div>
          </div>

          {purchasesAvailable ? (
            <>
              <button
                data-testid="button-unlock-pro"
                onClick={subscribe}
                disabled={busy}
                className="v2-neon-cta w-full mt-4 py-3 inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {busy ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Opening checkout
                  </>
                ) : (
                  <>
                    <Crown className="w-4 h-4" /> Unlock Pro
                  </>
                )}
              </button>
              <p className="mt-2 text-center text-[11px] font-semibold" style={{ color: "var(--text-secondary)" }}>
                {safePlan === "yearly"
                  ? `${formatPlanLine(prices.annual, "/year")} (save 17%). Cancel anytime.`
                  : `${formatPlanLine(prices.monthly, "/month")}. Cancel anytime.`}
              </p>
            </>
          ) : iapBlocked ? (
            <div
              className="mt-4 rounded-2xl p-3.5"
              data-testid="paywall-purchases-unavailable"
              style={{
                background: "hsl(43 96% 30% / 0.12)",
                border: "1px solid hsl(43 96% 50% / 0.45)",
              }}
            >
              <p className="text-sm font-semibold inline-flex items-center gap-1.5" style={{ color: "hsl(43 96% 70%)" }}>
                <AlertTriangle className="w-4 h-4" /> Purchases unavailable right now
              </p>
              <p className="text-[11px] mt-1 leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                {iapDiag?.summary ?? "The store could not be reached."} Your account is safe. Nothing was charged.
              </p>
              <button
                type="button"
                data-testid="button-paywall-open-diagnostics"
                onClick={() => {
                  closePaywall();
                  setLocation("/settings?open=diagnostics");
                }}
                className="mt-2.5 text-[11px] font-semibold underline underline-offset-2"
                style={{ color: "hsl(43 96% 72%)" }}
              >
                Open IAP diagnostics
              </button>
            </div>
          ) : null}

          <div className="mt-2 flex items-center justify-between gap-3">
            <button
              data-testid="button-restore-purchases"
              onClick={restore}
              disabled={busy}
              className="rounded-full py-2 text-[11px] transition-colors px-3"
              style={{ color: "var(--text-secondary)" }}
            >
              Restore purchases
            </button>
            <button
              data-testid="button-maybe-later"
              onClick={closePaywall}
              className="rounded-full py-2 text-sm transition-colors px-3"
              style={{ color: "var(--text-secondary)" }}
            >
              Maybe later
            </button>
          </div>

          {/* Apple Guideline 3.1.2 subscription disclosure + required legal
              links. Pro is an auto-renewable subscription, so the paywall must
              state the renewal terms and expose functional Terms of Use (EULA)
              and Privacy Policy links right where the purchase happens. */}
          <div className="mt-4 pt-3 border-t" style={{ borderColor: "var(--accent-fill-glow-subtle)" }}>
            <p className="text-center text-[10.5px] leading-relaxed px-1" style={{ color: "var(--text-secondary)" }}>
              Nexus Dex Pro is an auto-renewable subscription. Payment is charged
              to your {storeLabel} account at confirmation of purchase. It renews automatically for the same price and period
              unless cancelled at least 24 hours before the current period ends.
              Manage or cancel in your store account settings after purchase.
            </p>
            <div className="mt-2 flex items-center justify-center gap-2 text-[11px] font-semibold">
              <button
                type="button"
                data-testid="button-paywall-terms"
                onClick={() => setLegalTab("tos")}
                className="underline underline-offset-2"
                style={{ color: "var(--text-secondary)" }}
              >
                Terms of Use (EULA)
              </button>
              <span aria-hidden="true" style={{ color: "var(--text-secondary)" }}>·</span>
              <button
                type="button"
                data-testid="button-paywall-privacy"
                onClick={() => setLegalTab("privacy")}
                className="underline underline-offset-2"
                style={{ color: "var(--text-secondary)" }}
              >
                Privacy Policy
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

      <LegalReader
        open={legalTab !== null}
        initialTab={legalTab ?? "tos"}
        onClose={() => setLegalTab(null)}
      />
    </>
  );
}

function PlanCard({
  testid, active, onClick, eyebrow, price, unit, foot, badge, accent = "green",
}: {
  testid: string;
  active: boolean;
  onClick: () => void;
  eyebrow: string;
  price: string;
  unit: string;
  foot: string;
  badge?: string;
  accent?: "green" | "violet";
}) {
  const ring = accent === "violet" ? "hsl(280 70% 60%)" : "var(--accent-fill)";
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className="relative rounded-2xl p-3 text-left transition"
      style={{
        background: active
          ? "linear-gradient(135deg, var(--accent-fill) 0%, var(--accent-fill-hover) 100%)"
          : "var(--surface-dark-7)",
        border: `1.5px solid ${active ? ring : "var(--accent-fill-glow-subtle)"}`,
        boxShadow: active ? "0 0 18px var(--accent-fill-glow)" : "none",
      }}
    >
      {badge && (
        <span
          className="absolute -top-2 right-2 inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider"
          style={{
            background: accent === "violet"
              ? "linear-gradient(135deg, hsl(280 70% 55%), hsl(220 80% 55%))"
              : "linear-gradient(135deg, var(--accent-fill) 0%, var(--accent-fill-hover) 100%)",
            color: "var(--text-on-accent)",
          }}
        >
          {badge}
        </span>
      )}
      <p className="v2-display-up text-[9.5px]" style={{ color: active ? "var(--text-on-accent)" : "var(--text-secondary)" }}>{eyebrow}</p>
      <p className="font-bold text-lg tracking-tight tabular-nums mt-0.5" style={{ color: active ? "var(--text-on-accent)" : "var(--text-primary)" }}>
        {price}<span className="text-[10px] font-medium" style={{ color: active ? "var(--text-on-accent)" : "var(--text-secondary)" }}>{unit}</span>
      </p>
      <p className="text-[9px] mt-0.5" style={{ color: active ? "var(--text-on-accent)" : "var(--text-secondary)" }}>{foot}</p>
    </button>
  );
}

function BenefitRow({
  icon,
  title,
  desc,
}: { icon: React.ReactNode; title: string; desc: string }) {
  return (
    <div className="v2-neon-card-soft p-3.5 flex items-start gap-3">
      <div
        className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center"
        style={{
          background: "var(--accent-fill-glow-faint)",
          border: "1px solid var(--accent-fill-glow)",
          color: "var(--accent-fill)",
        }}
      >
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-sm font-semibold leading-tight" style={{ color: "var(--text-primary)" }}>{title}</p>
        <p className="text-[11px] mt-0.5 leading-snug" style={{ color: "var(--text-secondary)" }}>{desc}</p>
      </div>
      <Check className="w-4 h-4 shrink-0 mt-1" style={{ color: "var(--accent-fill)" }} />
    </div>
  );
}
